import React, { useState } from 'react';
import { QuestionMarkCircleIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Modal } from './ui';
import { useGuide } from '../guides/GuideContext';
import { guidesForRole } from '../guides/catalog';
import { usePolicy } from '../contexts/PolicyContext';
import { User } from '../types/auth';

interface HelpButtonProps {
  user: User;
}

/**
 * "?" in the header. Opens a list of things the person might be trying to do;
 * picking one starts a spotlight walkthrough on the real controls.
 */
const HelpButton: React.FC<HelpButtonProps> = ({ user }) => {
  const [open, setOpen] = useState(false);
  const { start } = useGuide();
  const { guidesEnabled } = usePolicy();
  const guides = guidesForRole(user.role);
  if (!guidesEnabled) return null;

  const startGuide = (id: string) => {
    setOpen(false);
    // Let the modal close before the overlay starts measuring.
    window.setTimeout(() => start(id), 50);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-guide="help-button"
        aria-label="Help: show me how to…"
        title="Help: show me how to…"
        className="p-2 text-clinic-muted hover:text-clinic-primary rounded-full hover:bg-clinic-primary-lighter transition-colors focus:outline-none focus:ring-2 focus:ring-clinic-primary"
      >
        <QuestionMarkCircleIcon className="h-6 w-6" />
      </button>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="What are you trying to do?"
        description="Pick one and we'll point at each step on the page."
        size="md"
      >
        {guides.length === 0 ? (
          <p className="text-sm text-clinic-muted">No guides for this area yet.</p>
        ) : (
          <ul className="divide-y divide-clinic-primary-lighter -my-2">
            {guides.map(g => (
              <li key={g.id}>
                <button
                  type="button"
                  onClick={() => startGuide(g.id)}
                  className="w-full flex items-center justify-between gap-3 py-3 text-left hover:bg-clinic-cream/40 rounded-lg px-2 -mx-2 transition-colors"
                >
                  <span>
                    <span className="block text-sm font-medium text-clinic-dark">{g.title}</span>
                    <span className="block text-xs text-clinic-muted">{g.description}</span>
                  </span>
                  <ChevronRightIcon className="h-4 w-4 text-clinic-muted flex-shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </>
  );
};

export default HelpButton;
